import React from 'react';
import { Search, Tag } from 'lucide-react';
import { Keyword } from './types';

interface MailControlsProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    timeFilter: string;
    onTimeFilterChange: (value: string) => void;
    timeFilters: { id: string, name: string, count: number }[];
    keywords: Keyword[];
    selectedKeyword: string;
    onKeywordSelect: (name: string) => void;
    onEditKeyword: (keyword: Keyword) => void;
    onDeleteKeyword: (id: string) => void;
    onToggleKeywordForm: () => void;
    showKeywordForm: boolean;
    getKeywordMatchCount: (name: string) => number;
}

const MailControls: React.FC<MailControlsProps> = ({
    searchTerm,
    onSearchChange,
    timeFilter,
    onTimeFilterChange,
    timeFilters,
    keywords,
    selectedKeyword,
    onKeywordSelect,
    onEditKeyword,
    onDeleteKeyword,
    onToggleKeywordForm,
    showKeywordForm,
    getKeywordMatchCount
}) => {
    return (
        <div className="mt-4 space-y-4 px-6">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Mail içeriğinde ara..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>

            <div className="space-y-4">
                {/* Time Filters */}
                <div className="flex flex-wrap gap-2">
                    {timeFilters.map(filter => (
                        <button
                            key={filter.id}
                            onClick={() => onTimeFilterChange(filter.id)}
                            className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${timeFilter === filter.id
                                    ? 'bg-blue-100 text-blue-800 border-blue-200'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                } border`}
                        >
                            {filter.name}
                            <span className="ml-2 bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                                {filter.count}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Keyword Filters */}
                <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-gray-900">Anahtar Kelimeler</h3>
                        <button
                            onClick={onToggleKeywordForm}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            {showKeywordForm ? 'İptal' : 'Yeni Ekle'}
                        </button>
                    </div>

                    {keywords.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {keywords.map((keyword) => {
                                const matchCount = getKeywordMatchCount(keyword.name);
                                return (
                                    <button
                                        key={keyword.id}
                                        onClick={() => onKeywordSelect(keyword.name)}
                                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${selectedKeyword === keyword.name
                                                ? 'bg-blue-500 text-white border border-blue-600 shadow-md'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                                            }`}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span className="font-medium">{keyword.name}</span>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${selectedKeyword === keyword.name
                                                    ? 'bg-white text-blue-600'
                                                    : matchCount > 0
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                {matchCount}
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onEditKeyword(keyword);
                                                }}
                                                className={`p-1 rounded ${selectedKeyword === keyword.name
                                                        ? 'text-white hover:text-blue-100 hover:bg-blue-600'
                                                        : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                                                    }`}
                                                title="Düzenle"
                                            >
                                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDeleteKeyword(keyword.id);
                                                }}
                                                className={`p-1 rounded ${selectedKeyword === keyword.name
                                                        ? 'text-white hover:text-red-100 hover:bg-red-600'
                                                        : 'text-red-600 hover:text-red-800 hover:bg-red-50'
                                                    }`}
                                                title="Sil"
                                            >
                                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <Tag className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500 text-sm">Henüz anahtar kelime eklenmemiş</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MailControls;
